import React, { useEffect, useRef } from 'react'
import { CircularProgress, Grid, Box, IconButton } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { Paper } from '@e3dc-react/shell/Elements/Paper'
import { useTypedSelector } from 'src/State/RootReducer'
import { J2Update } from '@e3dc-react/icons'
import useResizeObserver from 'use-resize-observer'
import { useFetchData } from 'src/Libs/system-data-handler/api/SystemDataFetching'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import { DateSettings } from 'src/Libs/system-data-handler/types'
import useOnScreen from '@e3dc-react/shell/Hooks/useOnScreen'
import { resetStoreHistoryData } from 'src/Libs/system-data-handler/state/store'
import useMappedCharts from '../hooks/use-mapped-charts'
import PieCharts from '../../default-charts/components/PieCharts'
import PresetCharts from '../../default-charts/components/PerformanceAndConsumptionCharts'
import CustomCharts from '../../custom-charts/components/CustomCharts'
import useDefaultAndCustomCharts from '../hooks/use-default-and-custom-charts'
import PerformanceValueChart from '../../default-charts/components/PerformanceValueChart'
import {
  FetchingState,
  getChartsEditingSettings,
  useChartDateBarContext,
  useLastTimeFetched,
  FabActionsButton,
  useUpdateEditedChartSettings,
} from 'src/Libs/chart-kit'
import { useIsWebviewMode, useUserSettings } from 'src/Hooks'
import { DASHBOARD_SYSTEM_CHARTS_ID } from 'src/Libs/qa/ids'
import { useIsElementVisible } from 'src/Libs/navigation'
import ChartLoadingBackdrop from 'src/Elements/ChartLoadingBackdrop'
import { useSystemChartsContext } from '../providers/SystemChartsProvider'
import { PerformanceAndConsumptionChartsProvider } from '../../default-charts/providers/PerformanceAndConsumptionChartsContext'
import useSystemCharts from '../hooks/use-system-charts'

interface StyleProps {
  trigger: boolean
  customRange: boolean
  isWebView: boolean
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  center: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: '100%',
    maxWidth: '100%',
    marginTop: theme.spacing(3),
  },
  chartsContainer: {
    marginTop: theme.spacing(2),
    width: '100%',
    userSelect: 'none',
    '& .react-grid-item > .react-resizable-handle.react-resizable-handle-se': {
      zIndex: 9999,
      color: theme.palette.secondary.main,
    },
    [theme.breakpoints.up('sm')]: {
      userSelect: 'auto',
    },
  },
  gridContainer: {
    zIndex: 5,
    position: 'relative',
  },
  paperPerformanceValues: {
    display: 'flex',
    '& .MuiSpeedDial-root': {
      bottom: props.isWebView ? '72px' : '12px',
    },
  },
}))

interface Props {
  storageId?: string
  installationId?: number
  hide: boolean
  showSavingsPieChart?: boolean
  isSavingsChartLoading?: boolean
}

/**
 * System charts
 *
 * This component integrates is to large for what it supposed to to do.
 * As it includes a lot of features, behaviours and responsibilities it looses control over essentials parts: what
 * should be re-rendered, and what not.
 *
 * TODO: this component needs to be split in a lot of small, custom components to control da reactivity flow
 *
 */
const SystemCharts: React.FC<Props> = ({ storageId, installationId, hide, showSavingsPieChart, isSavingsChartLoading }) => {
  const isWebView = useIsWebviewMode()
  const { t } = useCustomTranslation()
  const { ref: resizeRef } = useResizeObserver<HTMLDivElement>()
  const userId = useTypedSelector(state => state.auth.userId)
  const { isVisible, ref: visibilityRef } = useIsElementVisible()
  const { from, to, view, resolution, hasCustomRange, isForecastEnabled, setFrom, setTo, setView, setAreSystemChartsVisible } =
    useChartDateBarContext()
  const dateSettings: DateSettings = {
    from,
    to,
    view,
    isForecastEnabled,
    setFrom,
    setTo,
    setView,
  }

  const { classes } = useStyles({ trigger: false, customRange: hasCustomRange, isWebView })
  const { userSettings } = useUserSettings()
  const storeInitialised = useTypedSelector(state => state.storage.storeInitialised)
  const chartContainerRef = useRef(null)
  const visible = useOnScreen(chartContainerRef, { isFrozenOnceVisible: false })
  const chartSettings = getChartsEditingSettings(userSettings)
  const { defaultAndCustomCharts, layouts } = useDefaultAndCustomCharts(userSettings)
  const { saveCharts } = useUpdateEditedChartSettings(defaultAndCustomCharts)

  const rechartData = useFetchData({ storageId, from, to, view: resolution ?? view })

  const { handleSaveCharts } = useSystemCharts({ installationId: installationId ?? 0, storageId })
  const { resetChartSelection } = useSystemChartsContext()
  const { customMappedCharts, directConsumptionChart, directConsumptionProductionChart } = useMappedCharts({
    handleSaveCharts,
    defaultAndCustomCharts,
    installationId,
    storageId,
    chartSettings,
    dateSettings,
    rechartData,
  })

  const reloadDataHandler = (): void => {
    if (storageId) {
      resetStoreHistoryData(storageId)
    }
  }

  const chartLoadingIsCompleted = rechartData.loadingState.state === FetchingState.Completed
  const historyDataFetchDate = useLastTimeFetched()

  useEffect(() => {
    setAreSystemChartsVisible(isVisible && !hide)
  }, [isVisible, setAreSystemChartsVisible, hide])

  if (hide) return null

  if (defaultAndCustomCharts && storeInitialised && from && to) {
    return (
      <>
        <Grid item xs={12} className={classes.gridContainer} onClick={resetChartSelection}>
          <Paper
            paperRef={chartContainerRef}
            title={t('dashboard.liveView.performanceValues')}
            subTitle={historyDataFetchDate}
            className={classes.paperPerformanceValues}
            headerChildren={
              <IconButton onClick={() => reloadDataHandler()} size={'large'} aria-label={t('common.reload')}>
                <J2Update />
              </IconButton>
            }
            dataQa={DASHBOARD_SYSTEM_CHARTS_ID}
          >
            <Box ref={visibilityRef}>
              <Box className={classes.chartsContainer} ref={resizeRef}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <PieCharts
                      data={rechartData}
                      from={from}
                      to={to}
                      showForecast={isForecastEnabled}
                      view={view}
                      resolution={resolution}
                      handleSaveChart={handleSaveCharts}
                      showSavingsChart={showSavingsPieChart}
                      isSavingsChartLoading={isSavingsChartLoading}
                      isHistoryChartLoading={rechartData.loadingState.state === FetchingState.IsFetching}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    {directConsumptionChart && directConsumptionProductionChart && (
                      <PerformanceAndConsumptionChartsProvider>
                        <PresetCharts
                          performanceChart={
                            <PerformanceValueChart
                              data={rechartData}
                              from={from}
                              to={to}
                              showForecast={isForecastEnabled}
                              view={view}
                              resolution={resolution}
                              handleSaveChart={handleSaveCharts}
                            />
                          }
                          directConsumptionCharts={[directConsumptionChart, directConsumptionProductionChart]}
                        />
                      </PerformanceAndConsumptionChartsProvider>
                    )}
                  </Grid>
                </Grid>
                {!!customMappedCharts.length && <CustomCharts customCharts={customMappedCharts} />}
              </Box>
            </Box>
          </Paper>
          {rechartData.loadingState.state === FetchingState.IsFetching ? <ChartLoadingBackdrop loadingState={rechartData.loadingState} /> : null}
        </Grid>
        <FabActionsButton
          defaultAndCustomCharts={defaultAndCustomCharts}
          loadingState={rechartData.loadingState}
          installationId={installationId}
          layouts={layouts}
          storageId={storageId}
          chartLoadingIsCompleted={chartLoadingIsCompleted}
          saveCharts={props =>
            saveCharts({
              chartId: props.id,
              chartSettings: props,
              chartSettingsList: chartSettings,
              userId,
              installationId,
              storageSerialNumber: storageId,
              isInitial: false,
            })
          }
          visible={visible}
        />
      </>
    )
  }

  // loading...
  return (
    <Paper className={classes.center} elevation={1}>
      <CircularProgress />
    </Paper>
  )
}

export default SystemCharts
